import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { founders, collaboration, differentiators } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from 'sonner';
import {
  Trophy,
  Leaf,
  Award,
  Quote,
  ArrowRight,
  Clock,
  FileStack,
  Workflow,
  CheckCircle2,
  Sparkles,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const iconMap = {
  Trophy,
  Leaf,
  Award,
  Clock,
  FileStack,
  Workflow,
};

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.querySelector('#contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);
  };

  const handleHistoryClick = () => {
    navigate('/histoire');
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      <Header />
      
      {/* Hero Section - More compact */}
      <section className="relative pt-28 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#3dd3ee]/10 rounded-full blur-3xl animate-float" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-[#fad24b]/10 rounded-full blur-3xl animate-float-delayed" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div
            className={`text-center transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-[#61525a]/10 mb-6">
              <Users className="w-4 h-4 text-[#61525a]" />
              <span className="text-sm font-medium text-[#61525a]">Notre équipe</span>
            </span>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1e1919] mb-4">
              Qui sommes-<span className="text-[#3dd3ee]">nous</span> ?
            </h1>
            
            <p className="text-lg text-[#736c64] max-w-2xl mx-auto leading-relaxed">
              Indexcore est né de la rencontre de deux expertises complémentaires :
              <strong className="text-[#1e1919]"> la maîtrise des données sectorielles</strong> et{' '}
              <strong className="text-[#1e1919]">l'ingénierie avancée de l'IA</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Founders Section - Compact */}
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {founders.map((founder, index) => (
            <FounderCard key={founder.id} founder={founder} index={index} isVisible={isVisible} />
          ))}
        </div>
      </section>

      {/* Collaboration Section - Compact */}
      <section className="py-12 bg-[#1e1919] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3dd3ee]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#fad24b]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">
              {collaboration.title.split('Indexcore')[0]}
              <span className="text-[#3dd3ee]">Indexcore</span>
            </h2>
            <div className="inline-flex items-center gap-4 mt-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#fad24b]" />
              <span className="text-lg font-semibold text-[#fad24b]">{collaboration.subtitle}</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#fad24b]" />
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {collaboration.achievements.map((achievement, i) => (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-[#3dd3ee]/30 transition-all duration-300"
              >
                <div className="w-8 h-8 rounded-full bg-[#3dd3ee]/20 flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-4 h-4 text-[#3dd3ee]" />
                </div>
                <p className="text-[#bbb5ae] leading-relaxed text-sm">{achievement}</p>
              </div>
            ))}
          </div>
          
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#bbb5ae] leading-relaxed">
              {collaboration.conclusion.split('Indexcore')[0]}
              <strong className="text-white">Indexcore</strong>
              {collaboration.conclusion.split('Indexcore')[1]}
            </p>
          </div>
        </div>
      </section>

      {/* Differentiators Section - Compact */}
      <section className="py-12 bg-gradient-to-b from-[#f7f5f2] to-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#61525a] mb-4 shadow-sm">
              Pourquoi nous choisir
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1e1919]">
              Ce qui nous <span className="text-[#b4dc19]">différencie</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {differentiators.map((item, i) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={i}
                  className="group bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-500"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#b4dc19]/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-[#b4dc19]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e1919] mb-2">{item.title}</h3>
                  <p className="text-[#736c64] leading-relaxed text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-12 bg-[#61525a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Sparkles className="w-10 h-10 text-[#fad24b] mx-auto mb-4" />
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Prêt à transformer votre entreprise avec l'IA ?
          </h2>
          <p className="text-white/80 mb-6">
            Découvrez notre histoire et notre processus pour démarrer votre projet.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={handleHistoryClick}
              className="bg-[#3dd3ee] hover:bg-[#2bc4df] text-white px-6 py-5 transition-all duration-300 hover:scale-105"
            >
              Notre histoire
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={handleContactClick}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-6 py-5 transition-all duration-300"
            >
              Nous contacter
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

// Founder Card Component - More compact
const FounderCard = ({ founder, index, isVisible }) => {
  const isEven = index % 2 === 0;
  
  return (
    <div
      className={`mb-12 last:mb-0 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 200}ms` }}
    >
      <div className={`grid lg:grid-cols-2 gap-8 items-start ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
        {/* Image Side */}
        <div className={`${!isEven ? 'lg:order-2' : ''}`}>
          <div className="relative flex justify-center">
            <div
              className="absolute inset-0 rounded-full opacity-20 blur-xl max-w-[300px] max-h-[300px] mx-auto"
              style={{
                background: index === 0 
                  ? 'linear-gradient(135deg, #fad24b, #ff8c19)'
                  : 'linear-gradient(135deg, #3dd3ee, #b4dc19)',
              }}
            />
            <div className="relative">
              {/* Circular photo container */}
              <div 
                className="w-56 h-56 lg:w-64 lg:h-64 rounded-full shadow-xl border-4 border-white relative overflow-hidden"
              >
                <div 
                  className="absolute inset-0 bg-center bg-cover"
                  style={{
                    backgroundImage: `url(${founder.image})`,
                    backgroundPosition: index === 1 ? 'center 30%' : 'center center',
                    filter: index === 1 ? 'grayscale(100%)' : 'none',
                  }}
                />
              </div>
              {/* Name badge below photo */}
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold text-[#1e1919] mb-1">{founder.name}</h3>
                <p className="text-[#736c64] text-sm max-w-xs mx-auto">{founder.role}</p>
              </div>
            </div>
          </div>
          
          {/* Stats for Aniss - Compact */}
          {founder.stats && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              {founder.stats.map((stat, i) => (
                <div key={i} className="bg-white rounded-xl p-3 text-center shadow-md">
                  <div className="text-xl font-bold text-[#1e1919]">{stat.value}</div>
                  <div className="text-xs text-[#736c64]">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Content Side */}
        <div className={`${!isEven ? 'lg:order-1' : ''}`}>
          <div className="space-y-4">
            {/* Bio */}
            {founder.bio.map((paragraph, i) => (
              <p key={i} className="text-[#736c64] leading-relaxed text-sm">
                {paragraph}
              </p>
            ))}
            
            {/* Quote for Aymar */}
            {founder.quote && (
              <div className="relative bg-white rounded-xl p-4 shadow-md border-l-4 border-[#fad24b]">
                <Quote className="absolute top-3 right-3 w-6 h-6 text-[#fad24b]/20" />
                <p className="text-[#1e1919] italic leading-relaxed text-sm">
                  "{founder.quote}"
                </p>
              </div>
            )}
            
            {/* Achievements for Aymar */}
            {founder.achievements && (
              <div className="space-y-2">
                <h4 className="font-semibold text-[#1e1919] text-sm">Réalisations clés</h4>
                {founder.achievements.map((achievement, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#fad24b]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3 h-3 text-[#fad24b]" />
                    </div>
                    <span className="text-[#736c64] text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            )}
            
            {/* Clients for Aymar */}
            {founder.clients && (
              <div>
                <h4 className="font-semibold text-[#1e1919] mb-2 text-sm">Clients</h4>
                <div className="flex flex-wrap gap-2">
                  {founder.clients.map((client, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-[#f7f5f2] rounded-full text-xs text-[#736c64]"
                    >
                      {client}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Specialties for Aniss */}
            {founder.specialties && (
              <div className="space-y-2">
                <h4 className="font-semibold text-[#1e1919] text-sm">Expertises</h4>
                {founder.specialties.map((specialty, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#3dd3ee]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3 h-3 text-[#3dd3ee]" />
                    </div>
                    <span className="text-[#736c64] text-sm">{specialty}</span>
                  </div>
                ))}
              </div>
            )}
            
            {/* Awards for Aniss - Compact */}
            {founder.awards && (
              <div>
                <h4 className="font-semibold text-[#1e1919] mb-3 text-sm">Récompenses</h4>
                <div className="space-y-2">
                  {founder.awards.map((award, i) => {
                    const Icon = iconMap[award.icon];
                    const colors = ['#fad24b', '#b4dc19', '#3dd3ee'];
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${colors[i]}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: colors[i] }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs font-bold px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: `${colors[i]}20`, color: colors[i] }}
                            >
                              {award.rank}
                            </span>
                            <span className="font-semibold text-[#1e1919] text-sm truncate">{award.title}</span>
                          </div>
                          <p className="text-xs text-[#736c64] truncate">{award.organizer}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
